import { useBudgetConflictDetection } from '@/hooks/swapRoles/useBudgetConflictDetection';
import { type SwapModalData, useBudgetSwapModal } from '@/hooks/swapRoles/useBudgetSwapModal';
import { useManageBudgetGroupUsers } from '@/hooks/useManageBudgetGroupUsers';
import { useSnackbar } from '@/hooks/useSnackbar';

interface UseBudgetRoleManagementReturn {
  handleRoleChange: (userId: string, groupId: number, groupName: string, newRole: string) => Promise<void>;
  swapModalOpen: boolean;
  swapModalData: SwapModalData | null;
  closeSwapModal: () => void;
  confirmSwap: () => Promise<void>;
  isRoleChanging: boolean;
  isSwappingRole: boolean;
}

export function useBudgetRoleManagement(): UseBudgetRoleManagementReturn {
  const snackbar = useSnackbar();

  const { updateUser, loading: isUpdatingUser } = useManageBudgetGroupUsers({ snackbar });
  const { checkBudgetConflict } = useBudgetConflictDetection();
  const { swapModalOpen, swapModalData, openSwapModal, closeSwapModal, confirmSwap, isSwappingRole } = useBudgetSwapModal();

  const handleRoleChange = async (userId: string, groupId: number, groupName: string, newRole: string) => {
    if (isUpdatingUser) {
      return;
    }

    try {
      // Special handling for Distributor role - check for conflicts
      if (newRole === 'Distributor') {
        const conflict = await checkBudgetConflict(userId, groupId);

        if (conflict.hasConflict) {
          openSwapModal({
            userId,
            userEmail: userId,
            currentGroupId: conflict.conflictGroupId!,
            currentGroupName: conflict.conflictGroupName!,
            currentGroupType: conflict.conflictGroupType,
            newGroupId: groupId,
            newGroupName: groupName,
          });

          return;
        }
      }

      // Execute role change using updateUser
      const result = await updateUser(groupId, userId, newRole);

      if (result) {
        snackbar.showSuccess('User role updated successfully');
      }
    } catch (err) {
      // Error handling is already done in useManageBudgetGroupUsers
      console.error('Failed to update user role:', err);
    }
  };

  return {
    handleRoleChange,
    swapModalOpen,
    swapModalData,
    closeSwapModal,
    confirmSwap,
    isRoleChanging: isUpdatingUser,
    isSwappingRole,
  };
}
